<template>
  <transition name="zoom">
    <div v-if="visible" class="message" :class="className">
      <menu v-if="allowDismiss" class="message-menu">
        <FaIconButton
          icon="times"
          class="dismiss-button"
          :title="$t('components.message.dismiss-button')"
          @click="dismiss"
        />
      </menu>
      <component :is="`h${headingSize}`" v-if="hasHeading" class="heading">
        <slot name="heading" />
      </component>
      <slot />
    </div>
  </transition>
</template>

<script>
import FaIconButton from './FaIconButton'

export const Variants = {
  DEFAULT: 'default',
  ERROR: 'error',
  INVERTED: 'inverted',
}

/**
 * Displays a message in a nice box.
 * Optionally displays a dismiss button and emits the dismiss event when the button is clicked.
 * Use the visible prop to toggle visibility with a nice little animation.
 */
export default {
  name: 'Message',
  components: { FaIconButton },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: Variants.DEFAULT,
      validator(variant) {
        return Object.values(Variants).includes(variant)
      },
    },
    allowDismiss: {
      type: Boolean,
      default: false,
    },
    headingSize: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return { Variants }
  },
  computed: {
    hasHeading() {
      return this.$slots.heading
    },
    className() {
      return `${this.variant} ${this.hasHeading ? 'has-heading' : ''}`
    },
  },
  methods: {
    dismiss() {
      this.$emit('dismiss')
    },
  },
}
</script>

<style lang="scss" scoped>
.zoom-enter-active,
.zoom-leave-active {
  transition: all $transitionFast ease-out;
}

.zoom-enter,
.zoom-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.message {
  position: relative;
  padding: 0.5em 1.5em 0.5em;
  margin: 1em 0;

  > .heading {
    margin-top: 0.25em;
    font-size: 1.5em;
  }

  &.has-heading {
    padding-top: 1em;
  }

  &.default {
    background-color: darken($infoBackground, 10%);
  }

  &.inverted {
    background-color: lighten($textColor, 10%);
    color: $frameBackgroundColor;
  }

  &.error {
    background-color: desaturate(darken(tomato, 10%), 42%);
    color: $frameBackgroundColor;
  }
}

.message-menu {
  position: absolute;
  right: 1em;
  top: 1em;
}

.dismiss-button {
  color: inherit;
}
</style>
